import type { OpeningSideHolePlacement } from "building-types/openings/opening";
import type { OpeningSide, OpeningSideContext } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { MergedWindowSettings, GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { planeCopy, planeNormal } from "geom/plane";
import { polyLine2Reverse, polyLine2Shift } from "geom/polyLine2";
import { polyLine3OnPlane } from "geom/polyLine3";
import { makeWindowSide } from "../window/windowElevation";
import { windowHasJamb, windowHasWrapping, windowHasSill, windowCasingClipsFinish } from "../window/windowQuery";
import { planExtentsFromElevations } from "./openingUtils";
import { wallOpeningNew } from "./wallOpeningNew";

export function windowSide(
  context: OpeningSideContext,
  window: GeneratedWindowPlacement,
  settings: MergedWindowSettings,
  placement: OpeningSideHolePlacement,
  profileRectangular: OpeningSide,
  mirror: boolean,
): OpeningSide {
  const openingPlane = planeCopy(placement.openingPlane);

  const profiles = makeWindowSide(
    {
      ...context,
      openingPlane,
    },
    placement,
    profileRectangular,
    window,
    settings,
    mirror,
  );

  profiles.opening3 = polyLine3OnPlane(profiles.opening2, openingPlane);
  profiles.openingCasing3 = polyLine3OnPlane(profiles.openingCasing2, openingPlane);

  return profiles;
}
export function windowOpeningNew(
  window: GeneratedWindowPlacement,
  settings: MergedWindowSettings,
  frontPlacement: OpeningSideHolePlacement,
  frontProfileRectangular: OpeningSide,
  backPlacement: OpeningSideHolePlacement,
  backProfileRectangular: OpeningSide,
  mirror: boolean,
): WallOpening {
  const frontPlane = planeCopy(frontPlacement.openingPlane);
  const backPlane = planeCopy(backPlacement.openingPlane);

  const frontProfile = windowSide({}, window, settings, frontPlacement, frontProfileRectangular, true);
  const backProfile = windowSide({}, window, settings, backPlacement, backProfileRectangular, false);

  const [px, py] = planeNormal(frontPlane);

  const planExtents = planExtentsFromElevations([px, py], frontProfile.opening3, backProfile.opening3);
  const planExtentsCasing = planExtentsFromElevations(
    [px, py],
    frontProfile.openingCasing3,
    backProfile.openingCasing3,
  );

  if (window.reverse) {
    polyLine2Reverse(planExtents);
    polyLine2Shift(planExtents, 1);
    polyLine2Reverse(planExtentsCasing);
    polyLine2Shift(planExtentsCasing, 1);
  }

  return wallOpeningNew({
    handle: { entityType: "WINDOW", id: window.id },
    hasExteriorCasing: window.hasExteriorCasing,
    casingClipsFinish: windowCasingClipsFinish(window, window.exteriorCasing as ExteriorWindowCasingSettings),
    hasJamb: windowHasJamb(window),
    hasWrapping: windowHasWrapping(window),
    hasSill: windowHasSill(window.descriptor),
    front: frontProfile,
    back: backProfile,
    frontPlane,
    backPlane,
    frontRoom: frontPlacement.room,
    backRoom: backPlacement.room,
    planExtents,
    planExtentsCasing,
    mirror,
  });
}
